import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Container } from '../../../../Components';
import { ModuleContext } from '../../../../Context/ModuleContext/ModuleContext';
import { CarryLK } from './CarryLK';
import { OldCarryLK } from './CarryLK/oldindex';
import PlanningLK from './PlanningLK';
import { OldPlanningLK } from './PlanningLK/oldindex';
import { ReportsLK } from './ReportsLK';
import { CreatePlannig } from './PlanningLK/CreatePlanning';
import { CreateLawComplianceReport } from './CarryLK/Compliance/Create';
import { useAptorApi } from '../../../../Api';
import { ConfirmationSessionProvider } from '../../../../Context/ComplianceSessionContext';
import { Module, UserContext } from "../../../../Context/UserContext/UserContext";
import { Observation } from './Observations';

export interface ILawInfo {
  name: string;
  number: string;
  category: string;
}

const ComplianceView = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0');
  const { api, abortController } = useAptorApi();
  const [info, setInfo] = useState<ILawInfo>();

  useEffect(() => {
    const checkType = window.location.pathname.split("/");
    if (checkType?.length && checkType[5] === "other-requirements") {
      api.getLawPortalRequirementInfo<ILawInfo>(id).then((result) => {
        if (abortController.current.signal.aborted) {
          return;
        }
        setInfo(result);
      });
    } else {
      api.getLawPortalLawInfo<ILawInfo>(id).then((result) => {
        if (abortController.current.signal.aborted) {
          return;
        }
        setInfo(result);
      });
    }
  }, [api, abortController, id]);

  if (info === undefined) {
    return null;
  }

  return <CreateLawComplianceReport info={info} />;
};

export const LegalComplianceControl = () => {
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const { hasAccessToModule, customerFeatures, applicationVersion } = useContext(UserContext);
  const isOldVersion = applicationVersion?.value === 1;

  useEffect(() => {
    const hasAvvikelserLight = customerFeatures?.some((feature: any) => feature.value === 13);
    const menuKeySuffix = isOldVersion ? '.lek.old' : '.lek';
    let menu: any = [];
    if (hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization)) {
      menu = [
        {
          title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.planning${menuKeySuffix}` }),
          to: `/law-portal/legal-compliance-control/planning`,
        },
        {
          title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.carry${menuKeySuffix}` }),
          to: `/law-portal/legal-compliance-control/carry`,
        },
        {
          title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.reports${menuKeySuffix}` }),
          to: `/law-portal/legal-compliance-control/reports`,
        }
      ];
    } else {
      menu = [
        {
          title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.carry${menuKeySuffix}` }),
          to: `/law-portal/legal-compliance-control/carry`,
        },
        {
          title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.reports${menuKeySuffix}` }),
          to: `/law-portal/legal-compliance-control/reports`,
        }
      ];
    }
    if (!isOldVersion && hasAvvikelserLight) {
      menu.push({
        title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.observations' }),
        to: `/law-portal/legal-compliance-control/observations`,
      })
    }
    setHeader(
      formatMessage({
        id: isOldVersion ? 'law-portal.legal.compliance.header' : 'law-portal.legal.compliance.header',
        defaultMessage: isOldVersion ? 'Legal compliance control' : 'Evaluation control',
      }),
      menu,
    );
  }, [setHeader, formatMessage, hasAccessToModule, customerFeatures, isOldVersion]);

  const hasAdminAccess = hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization);

  return (
    <Container fullWidth>
      <ConfirmationSessionProvider>
        <Routes>
          <Route path="carry" element={isOldVersion ? <OldCarryLK /> : <CarryLK />} />
          <Route path="plan/:planid/law/:id/compliance/report" element={<ComplianceView />} />
          <Route path="plan/:planid/law/:id/compliance/report/edit" element={<ComplianceView />} />
          <Route path="plan/:planid/other-requirements/:id/compliance/report" element={<ComplianceView />} />
          <Route path="plan/:planid/other-requirements/:id/compliance/report/edit" element={<ComplianceView />} />
          <Route path="planning" element={isOldVersion ? <OldPlanningLK /> : <PlanningLK />} />
          <Route path="reports" element={<ReportsLK />} />
          <Route path="observations" element={<Observation />} />
          <Route
            index
            element={
              <Navigate to="carry" replace />
            }
          />
        </Routes>
      </ConfirmationSessionProvider>
    </Container>
  );
};